<template>
  <div class="kpi-skel" :class="[`kpi-skel--${variant}`, `kpi-skel--${size}`]">
    <!-- Hero: big card with title bar + value area + progress bar -->
    <template v-if="variant === 'hero'">
      <div class="kpi-skel-hero">
        <div class="kpi-skel-line kpi-skel-line--sm kpi-skel-line--w25 shimmer"></div>
        <div class="kpi-skel-line kpi-skel-line--xxl kpi-skel-line--w60 shimmer" style="margin-top:6px"></div>
        <div class="kpi-skel-line kpi-skel-line--sm kpi-skel-line--w40 shimmer" style="margin-top:4px"></div>
        <div class="kpi-skel-bar shimmer" style="margin-top:10px"></div>
      </div>
    </template>

    <!-- Dual hero: two hero cards side by side -->
    <template v-else-if="variant === 'dual-hero'">
      <div class="kpi-skel-dual">
        <div class="kpi-skel-hero">
          <div class="kpi-skel-line kpi-skel-line--sm kpi-skel-line--w30 shimmer"></div>
          <div class="kpi-skel-line kpi-skel-line--xxl kpi-skel-line--w50 shimmer" style="margin-top:6px"></div>
          <div class="kpi-skel-line kpi-skel-line--sm kpi-skel-line--w35 shimmer" style="margin-top:4px"></div>
          <div class="kpi-skel-bar shimmer" style="margin-top:10px"></div>
        </div>
        <div class="kpi-skel-hero">
          <div class="kpi-skel-line kpi-skel-line--sm kpi-skel-line--w25 shimmer"></div>
          <div class="kpi-skel-line kpi-skel-line--xxl kpi-skel-line--w55 shimmer" style="margin-top:6px"></div>
          <div class="kpi-skel-line kpi-skel-line--sm kpi-skel-line--w30 shimmer" style="margin-top:4px"></div>
          <div class="kpi-skel-bar shimmer" style="margin-top:10px"></div>
        </div>
      </div>
    </template>

    <!-- Strip: horizontal row of stat items -->
    <template v-else-if="variant === 'strip'">
      <div class="kpi-skel-strip">
        <div v-for="i in itemCount" :key="i" class="kpi-skel-stat">
          <div class="kpi-skel-line kpi-skel-line--lg kpi-skel-line--w45 shimmer"></div>
          <div class="kpi-skel-line kpi-skel-line--xs kpi-skel-line--w60 shimmer" style="margin-top:4px"></div>
          <div v-if="size === 'lg'" class="kpi-skel-line kpi-skel-line--xs kpi-skel-line--w35 shimmer" style="margin-top:3px"></div>
        </div>
      </div>
    </template>

    <!-- Table: header row + data rows -->
    <template v-else-if="variant === 'table'">
      <div class="kpi-skel-table">
        <div class="kpi-skel-table-row kpi-skel-table-row--head">
          <div v-for="i in colCount" :key="'h'+i" class="kpi-skel-line kpi-skel-line--sm shimmer"></div>
        </div>
        <div v-for="row in itemCount" :key="'r'+row" class="kpi-skel-table-row">
          <div v-for="col in colCount" :key="'c'+col" class="kpi-skel-line" :class="col === 1 ? 'kpi-skel-line--md kpi-skel-line--w60' : 'kpi-skel-line--sm kpi-skel-line--w40'">
            <div class="shimmer"></div>
          </div>
        </div>
      </div>
    </template>

    <!-- Pills: row of circular/badge-shaped items -->
    <template v-else-if="variant === 'pills'">
      <div class="kpi-skel-pills">
        <div v-for="i in itemCount" :key="i" class="kpi-skel-pill shimmer"></div>
      </div>
    </template>

    <!-- Tops list -->
    <template v-else-if="variant === 'tops'">
      <div class="kpi-skel-tops">
        <div v-for="i in itemCount" :key="i" class="kpi-skel-top-row">
          <div class="kpi-skel-icon shimmer"></div>
          <div class="kpi-skel-top-content">
            <div class="kpi-skel-line kpi-skel-line--md kpi-skel-line--w70 shimmer"></div>
            <div class="kpi-skel-line kpi-skel-line--xs kpi-skel-line--w45 shimmer" style="margin-top:3px"></div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'KpiSectionSkeleton',
  props: {
    variant: {
      type: String,
      default: 'hero',
      validator: v => ['hero', 'dual-hero', 'strip', 'table', 'pills', 'tops'].includes(v),
    },
    size: {
      type: String,
      default: 'md',
      validator: v => ['sm', 'md', 'lg'].includes(v),
    },
    itemCount: {
      type: Number,
      default: 3,
    },
    colCount: {
      type: Number,
      default: 4,
    },
  },
};
</script>

<style scoped>
/* ── Container ── */
.kpi-skel {
  width: 100%;
}

/* ── Shimmer animation ── */
.shimmer {
  position: relative;
  overflow: hidden;
  border-radius: 6px;
  background: var(--skel-bg, rgba(148, 163, 184, 0.28));
}

.shimmer::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.6) 50%,
    transparent 100%
  );
  animation: kpi-shimmer 1.6s ease-in-out infinite;
  background-size: 200% 100%;
}

/* Dark mode: usamos :deep() porque .dark está fuera del componente scoped */
:deep(.dark) .shimmer {
  --skel-bg: rgba(113, 113, 122, 0.18);
}

:deep(.dark) .shimmer::after {
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.15) 50%,
    transparent 100%
  );
  background-size: 200% 100%;
}

@keyframes kpi-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ── Line sizes ── */
.kpi-skel-line--xs { height: 10px; }
.kpi-skel-line--sm { height: 14px; }
.kpi-skel-line--md { height: 18px; }
.kpi-skel-line--lg { height: 22px; }
.kpi-skel-line--xl { height: 28px; }
.kpi-skel-line--xxl { height: 36px; }

.kpi-skel-line--w25 { width: 25%; }
.kpi-skel-line--w30 { width: 30%; }
.kpi-skel-line--w35 { width: 35%; }
.kpi-skel-line--w40 { width: 40%; }
.kpi-skel-line--w45 { width: 45%; }
.kpi-skel-line--w50 { width: 50%; }
.kpi-skel-line--w55 { width: 55%; }
.kpi-skel-line--w60 { width: 60%; }
.kpi-skel-line--w70 { width: 70%; }

/* ── Hero ── */
.kpi-skel-hero {
  border-radius: 8px;
  padding: 12px 14px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  background: var(--skel-card-bg, rgba(248, 250, 252, 0.85));
}

.dark .kpi-skel-hero {
  background: rgba(39, 39, 42, 0.5);
  border-color: rgba(82, 82, 91, 0.3);
}

.kpi-skel-bar {
  height: 6px;
  border-radius: 99px;
  width: 100%;
}

/* ── Dual hero ── */
.kpi-skel-dual {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

@media (max-width: 640px) {
  .kpi-skel-dual {
    grid-template-columns: 1fr;
  }
}

/* ── Strip ── */
.kpi-skel-strip {
  display: flex;
  align-items: stretch;
  gap: 0;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  background: var(--skel-card-bg, rgba(248, 250, 252, 0.85));
  overflow: hidden;
}

.dark .kpi-skel-strip {
  background: rgba(39, 39, 42, 0.5);
  border-color: rgba(82, 82, 91, 0.3);
}

.kpi-skel-stat {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 11px 14px;
  gap: 4px;
}

.kpi-skel-stat + .kpi-skel-stat {
  border-left: 1px solid rgba(148, 163, 184, 0.2);
}

.dark .kpi-skel-stat + .kpi-skel-stat {
  border-left-color: rgba(82, 82, 91, 0.3);
}

/* ── Table ── */
.kpi-skel-table {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.2);
  background: var(--skel-card-bg, rgba(248, 250, 252, 0.85));
}

.dark .kpi-skel-table {
  background: rgba(39, 39, 42, 0.5);
  border-color: rgba(82, 82, 91, 0.3);
}

.kpi-skel-table-row {
  display: flex;
  gap: 12px;
  padding: 10px 14px;
  align-items: center;
}

.kpi-skel-table-row--head {
  border-bottom: 1px solid rgba(148, 163, 184, 0.2);
  padding: 8px 14px;
}

.dark .kpi-skel-table-row--head {
  border-bottom-color: rgba(82, 82, 91, 0.3);
}

.kpi-skel-table-row + .kpi-skel-table-row {
  border-top: 1px solid rgba(148, 163, 184, 0.08);
}

.kpi-skel-table-row > .kpi-skel-line {
  flex: 1;
}

/* ── Pills ── */
.kpi-skel-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.kpi-skel-pill {
  height: 28px;
  width: 120px;
  border-radius: 999px;
}

/* ── Tops list ── */
.kpi-skel-tops {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.kpi-skel-top-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border: 1px solid rgba(148, 163, 184, 0.15);
  border-radius: 8px;
  background: var(--skel-card-bg, rgba(248, 250, 252, 0.7));
}

.dark .kpi-skel-top-row {
  background: rgba(39, 39, 42, 0.35);
  border-color: rgba(82, 82, 91, 0.25);
}

.kpi-skel-icon {
  width: 18px;
  height: 18px;
  border-radius: 999px;
  flex-shrink: 0;
}

.kpi-skel-top-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
</style>
